import React from "react";
import { Reveal, Eyebrow } from "@/components/ui-bits";
import { PageHero, Section } from "@/components/PageShell";
import { IMG } from "@/data/images";

const PILLARS = [
  ["Responsible natural-diamond origin", "Every recorded diamond begins with lawful documentation and a verified Botswana source."],
  ["Botswana craftsmanship", "Planning, cutting and polishing performed by skilled hands within Botswana's own facilities."],
  ["Citizen participation", "Value that flows to the people and communities connected to each diamond's origin."],
  ["Skills & technology transfer", "Advanced identification and grading capability, built and retained locally."],
  ["Long-term value creation", "Turning natural endowment into enduring human progress."],
  ["A living connection", "Between the diamond and the people and country of origin."],
];

export default function BotswanaJourney() {
  return (
    <div>
      <PageHero
        eyebrow="The Botswana Journey"
        title="A precious inheritance, responsibly carried forward."
        subtitle="Botswana's diamonds have helped transform natural endowment into enduring human progress, strengthening communities and supporting the responsible guardianship of its landscapes and wildlife."
        image={IMG.okavangoTree}
      />

      <Section>
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="relative rounded-2xl overflow-hidden aspect-[4/5]">
              <img src={IMG.portrait1} alt="A dignified portrait" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-bb-bg/70 to-transparent" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div>
              <Eyebrow>The deeper message</Eyebrow>
              <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-tight">
                Not only a precious object. Part of a covenant.
              </h2>
              <p className="mt-6 text-base text-bb-silver/70 leading-relaxed">
                A Botswana diamond is part of a long-standing covenant between natural resources, human progress and responsible
                stewardship. Its true brilliance is measured not only by the light it returns, but by the value it can return
                to people, to place and to future generations.
              </p>
              <p className="mt-6 font-serif text-2xl italic text-bb-gold/90">
                "Every diamond has an origin. A Botswana diamond has a legacy."
              </p>
            </div>
          </Reveal>
        </div>
      </Section>

      <Section className="pt-0">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PILLARS.map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.08}>
              <div className="glass rounded-2xl p-7 h-full">
                <div className="font-serif text-bb-gold text-2xl">{`0${i + 1}`}</div>
                <h3 className="mt-4 font-serif text-2xl leading-tight">{t}</h3>
                <p className="mt-2 text-sm text-bb-silver/65 leading-relaxed">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section className="pt-0">
        <Reveal><Eyebrow>How origin is described</Eyebrow></Reveal>
        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {[
            ["Botswana Origin", "Mined in Botswana."],
            ["Crafted in Botswana", "Mined and manufactured in Botswana."],
            ["Botswana Brilliance Verified", "Its evidence has been reviewed and connected through the Botswana Brilliance certificate."],
          ].map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.1}>
              <div className="glass rounded-2xl p-7 h-full">
                <h3 className="font-serif text-2xl">{t}</h3>
                <p className="mt-2 text-sm text-bb-silver/65 leading-relaxed">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <section className="relative py-32 overflow-hidden grain">
        <img src={IMG.savannah} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-bb-bg via-bb-bg/80 to-bb-bg/50" />
        <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-6xl leading-tight">
              A diamond shaped by the land.<br /><span className="gold-text italic">A legacy shared with its people.</span>
            </h2>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
